/**
 * Nuclear facility layer renderer -- matching Worldmonitor 1:1.
 * Uses IconLayer with hexagon shape (mask:true via white SVG atlas).
 * Yellow/gold default, red for contested facilities.
 * Decommissioned facilities are filtered out.
 *
 * MIT License -- see THIRD-PARTY-NOTICES.md
 */

import { IconLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { NUCLEAR_FACILITIES } from '@/config/data';
import type { NuclearFacility } from '@/config/data';
import { MARKER_ICONS, NUCLEAR_ICON_MAPPING } from '@/map/layer-colors';

/** Layer IDs handled by this renderer. */
export const NUCLEAR_LAYER_IDS = new Set(['nuclear']);

/** Pre-filter: exclude decommissioned facilities. */
const ACTIVE_NUCLEAR = NUCLEAR_FACILITIES.filter(f => f.status !== 'decommissioned');

/** Create deck.gl layers for nuclear facility markers -- hexagon icons. */
export function createNuclearLayers(
  _events: unknown[],
  _zoom: number,
): Layer[] {
  return [
    new IconLayer<NuclearFacility>({
      id: 'nuclear-layer',
      data: ACTIVE_NUCLEAR,
      getPosition: (d) => [d.lon, d.lat],
      getIcon: () => 'hexagon',
      iconAtlas: MARKER_ICONS.hexagon,
      iconMapping: NUCLEAR_ICON_MAPPING,
      getSize: 14,
      getColor: (d) => {
        if (d.status === 'contested') return [255, 50, 50, 200];
        return [255, 220, 0, 200];
      },
      sizeScale: 1,
      sizeMinPixels: 10,
      sizeMaxPixels: 22,
      pickable: true,
    }),
  ];
}
